import React from 'react';
import styled from 'styled-components';
import {
  useAllProjectsTelStatusWeeklyReportsQuery,
  useTelStatusesByClientIdQuery,
} from 'api';
import useSearchParams from '../useSearchParams';
import moment from 'moment';
import {StatusEclipse} from 'components/Ui';
import telCategoryColor from 'helpers/telCategoryColor';
import _ from 'lodash';
import {Button} from 'components/antd';
import {ArrowRight, Download} from 'components/Ui/Icon';
import DownloadModal from './PreleadReportDownloadModal';
import {telStatusCategoryHash} from 'helpers/telStatusCategoryHash';

export default () => {
  const [isModalOpen, setModalOpen] = React.useState(false);
  const {query, searchParams, setQuery} = useSearchParams();
  const [displayCategories, setDisplayCategories] = React.useState([]);
  const {data: {allProjectsTelStatusWeeklyReports = []} = {}} =
    useAllProjectsTelStatusWeeklyReportsQuery({
      variables: {
        search: searchParams,
      },
      fetchPolicy: 'network-only',
    });

  const {data: {telStatusesByClientId = []} = {}} =
    useTelStatusesByClientIdQuery({
      variables: {
        search: searchParams,
      },
      fetchPolicy: 'network-only',
    });

  const categorizedTelStatuses = React.useCallback(
    (category: string) =>
      telStatusesByClientId.filter(
        (telStatus) => telStatus.category === category,
      ),
    [telStatusesByClientId],
  );

  const totalCount = (object: any): any => {
    const value = Object.values(object).reduce(
      (total: number, value: number) => total + value,
      0,
    );
    if (!value) return null;

    return value;
  };

  const toggle = React.useCallback(
    (category: string) => {
      if (displayCategories.includes(category)) {
        setDisplayCategories(
          displayCategories.filter(
            (displayCategory) => displayCategory != category,
          ),
        );
      } else {
        setDisplayCategories([...displayCategories, category]);
      }
    },
    [displayCategories],
  );

  if (allProjectsTelStatusWeeklyReports.length === 0) return null;

  let weeklyCounts: {[key: string]: number} = {};
  allProjectsTelStatusWeeklyReports.forEach((report) => {
    const dailyCount =
      totalCount(report.appoint) +
      totalCount(report.call) +
      totalCount(report.callNg) +
      totalCount(report.contact) +
      totalCount(report.contactNg) +
      totalCount(report.document) +
      totalCount(report.other) +
      totalCount(report.reception) +
      totalCount(report.receptionNg);
    weeklyCounts[report.countDate] = dailyCount;
  });

  let telCategoryCounts: {[key: string]: number} = {};
  Object.keys(telStatusCategoryHash).forEach((category) => {
    telCategoryCounts[category] = allProjectsTelStatusWeeklyReports.reduce(
      (total: number, report: any) =>
        total + totalCount(report[_.camelCase(category)]),
      0,
    );
  });

  let telStatusCounts: {[key: string]: number} = {};
  Object.keys(telStatusCategoryHash).forEach((category) => {
    categorizedTelStatuses(category).forEach((telStatus) => {
      telStatusCounts[telStatus.id] = allProjectsTelStatusWeeklyReports.reduce(
        (total: number, report: any) =>
          report[category]?.[telStatus.id]
            ? total + report[category][telStatus.id]
            : total,
        0,
      );
    });
  });

  return (
    <Container>
      <div>
        <h2>ステータス・カテゴリ別</h2>
        <div>
          <Display>
            <span>
              {query.date
                ? moment(query.date).format('YYYY年MM月')
                : moment().format('YYYY年MM月')}
            </span>
            <Button
              type="text"
              className="left"
              onClick={() =>
                setQuery({
                  date: moment(query.date)
                    .subtract(1, 'months')
                    .format('YYYY-MM-DD'),
                })
              }>
              <ArrowRight />
            </Button>
            <Button
              type="text"
              onClick={() =>
                setQuery({
                  date: moment(query.date)
                    .add(1, 'months')
                    .format('YYYY-MM-DD'),
                })
              }>
              <ArrowRight />
            </Button>

            <Button
              className="today"
              onClick={() => setQuery({date: moment().format('YYYY-MM-DD')})}>
              今月
            </Button>
          </Display>

          <DownloadButton onClick={() => setModalOpen(true)}>
            <Download />
            ダウンロード
          </DownloadButton>
        </div>
      </div>

      <Table>
        <div className="column far-left">
          <div className="far-top">
            <span>
              {query.date
                ? moment(query.date).format('YYYY年MM月')
                : moment().format('YYYY年MM月')}
            </span>
            <div className="all-count">
              {totalCount(weeklyCounts) ? totalCount(weeklyCounts) : 0}
            </div>
          </div>

          {Object.keys(telStatusCategoryHash).map((category) => (
            <React.Fragment key={category}>
              <div
                className={
                  'category ' + (!category.includes('ng') && 'openable')
                }
                onClick={() => toggle(category)}>
                {!category.includes('ng') && (
                  <Open
                    category={category}
                    displayCategories={displayCategories}>
                    <ArrowRight />
                  </Open>
                )}
                <StatusEclipse color={telCategoryColor(category)} />
                <span>{telStatusCategoryHash[category]}</span>
                <span className="category-count">
                  {telCategoryCounts[category]}
                </span>
              </div>
              {displayCategories.includes(category) &&
                !category.includes('ng') &&
                categorizedTelStatuses(category).map((telStatus) => (
                  <div key={telStatus.id}>
                    <span>{telStatus.name}</span>
                    <span className="category-count">
                      {telStatusCounts[telStatus.id]}
                    </span>
                  </div>
                ))}
            </React.Fragment>
          ))}
        </div>

        {allProjectsTelStatusWeeklyReports.map((report: any) => (
          <div className="column" key={report.countDate}>
            <div
              className={
                'far-top ' +
                (moment(report.countDate).isSame(new Date(), 'isoWeek') &&
                  'this-week')
              }>
              <span>W{moment(report.countDate).isoWeek()}</span>
              <span>
                {moment(report.countDate).startOf('isoWeek').format('MM/DD')} -{' '}
                {moment(report.countDate).endOf('isoWeek').format('MM/DD')}
              </span>
              <div className="weekly-count">
                {weeklyCounts[report.countDate]}
              </div>
            </div>

            {Object.keys(telStatusCategoryHash).map((category) => (
              <React.Fragment key={category}>
                <div className="category">
                  <span>
                    {report[_.camelCase(category)] &&
                      totalCount(report[_.camelCase(category)])}
                  </span>
                </div>
                {displayCategories.includes(category) &&
                  !category.includes('ng') &&
                  categorizedTelStatuses(category).map((telStatus) => (
                    <div key={telStatus.id}>
                      <span>
                        {report[category] && report[category][telStatus.id]}
                      </span>
                    </div>
                  ))}
              </React.Fragment>
            ))}
          </div>
        ))}
      </Table>

      {isModalOpen && (
        <DownloadModal isOpen={isModalOpen} setModal={setModalOpen} />
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;

  > div:first-child {
    margin-bottom: 15px;
    display: flex;
    align-items: center;

    > h2 {
      margin: auto auto auto 0;
      font-weight: bold;
      font-size: 20px;
      line-height: 29px;
      color: var(--text-color-1);
    }

    > div {
      position: relative;
      margin: auto 0 auto auto;
      display: flex;
    }
  }
`;

const Display = styled.div`
  margin-left: 10px;

  > span {
    font-size: 12px;
    line-height: 17px;
    color: var(--text-color-1);
  }

  > button {
    padding: 5.6px 6px;

    svg {
      path {
        stroke: var(--text-color-2);
      }
    }

    &.left {
      svg {
        transform: rotate(180deg);
      }
    }

    &.today {
      margin-left: 10px;
      padding: 5.6px 10px;
      border: 1px solid #e1e6eb;
      box-sizing: border-box;
      border-radius: 4px;
    }
  }
`;

const DownloadButton = styled(Button)`
  position: absolute;
  top: -50px;
  right: 0px;

  display: flex;
  border: 1px solid #e1e6eb;
  border-radius: 4px;

  > svg {
    margin-right: 6px;
  }

  > span {
    margin: auto 0;
    font-weight: bold;
    font-size: 12px;
    color: var(--text-color-1);
  }
`;

const Table = styled.div`
  display: flex;
  border-left: 1px solid #e1e6eb;
  border-top: none;
  overflow-y: scroll;
  max-height: 600px;
  width: 969px;

  .column {
    display: flex;
    flex-direction: column;

    > div {
      width: 107px;
      min-height: 43px;
      border-right: 1px solid #e1e6eb;
      border-bottom: 1px solid #e1e6eb;

      display: flex;
      align-items: center;

      &:first-child {
        > span {
          margin: 0 auto;
        }

        &.far-top {
          top: 0;
          z-index: 3;
          position: sticky;
          background: #ffffff;
          border-top: 1px solid #e1e6eb;
          display: flex;
          flex-direction: column;
          align-items: center;
          min-height: 110px;

          span {
            &:first-child {
              margin: 10px auto 0;
            }
          }

          &.this-week {
            border-top: 2px solid var(--text-color-1);

            span {
              color: var(--text-color-1);
              font-weight: bold;
            }
          }
        }
      }

      &:nth-child(2) {
        border-top: none;
      }

      > span {
        margin: 0 9px 0 auto;
        color: var(--text-color-2);
      }

      &.category {
        > span {
          color: var(--text-color-1);
          font-weight: bold;
        }
      }
    }

    &:last-child {
      > div {
        border-right: 1px solid #e1e6eb;
      }
    }

    &.far-left {
      left: 0;
      z-index: 10;
      position: sticky;
      background: #ffffff;

      > div {
        width: 309px;
        display: flex;
        align-items: center;
        padding: 0 13px 0 40px;
        border-right: 3px double #e1e6eb;
        background: #fff;

        &.category {
          padding: 0 13px;

          svg {
            margin-right: 5px;
          }
          span {
            font-weight: bold;
          }
          &:last-child {
            border-bottom: 1px solid #e1e6eb;
          }
        }

        &.openable {
          cursor: pointer;
        }

        > span {
          margin: 0;
          font-size: 13px;
        }
      }
    }
    .all-count {
      padding-top: 36px;
      padding-bottom: 20px;
      margin: 0 0 0 auto;
      font-weight: bold;
    }
    .weekly-count {
      font-weight: bold;
      margin: 0 9px 0 auto;
      padding-top: 20px;
      padding-bottom: 20px;
    }
    .category-count {
      margin: 0 0 0 auto !important;
    }
  }
`;

const Open = styled.div<{category: string; displayCategories: any}>`
  display: flex;
  width: 13px;
  height: 13px;
  > svg {
    ${({category, displayCategories}) =>
      displayCategories.includes(category) && 'transform: rotate(90deg);'}

    width: 13px;
    height: 13px;
    fill: var(--text-color-3);
    path {
      width: 13px;
      height: 13px;
    }
  }
`;
